import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Error from "../components/Error";
import Card from "../components/Card";
import EmptyState from "../components/EmptyState";
import { SkeletonGrid } from "../components/Skeleton";
import { SearchFilters, FilterDrawer } from "../components/SearchFilters";
import { fetchEvents } from "../api";
import { STORAGE_NAME } from "../global";
import {
  parseFilters,
  hasAnyFilter,
  countActiveFilters,
  buildWhereClause,
  notFinishedClause,
  describeActiveFilters,
  parseSort,
  orderByFor,
  SORT_OPTIONS,
} from "../utils/searchFilters";
import "./Search.css";

const PAGE_SIZE = 20;
const LEGACY_STORAGE_KEY = STORAGE_NAME + ":search";

const fullDateFmt = new Intl.DateTimeFormat("fr-FR", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dayMonthFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
});

// Shortcuts offered while no filter is set — one tap instead of a blank form.
const SUGGESTIONS = [
  { label: "Gratuit ce week-end", params: { when: "weekend", price: "free" } },
  { label: "Concerts ce soir", params: { when: "today", cats: "music" } },
  { label: "Expos du moment", params: { cats: "expo" } },
  { label: "Avec les enfants", params: { cats: "family" } },
  { label: "Ateliers gratuits", params: { cats: "workshop", price: "free" } },
  { label: "Balades urbaines", params: { cats: "walk" } },
];

// Second line of the headline, in italics — follows the date filter.
function headlineSuffix(filters) {
  if (filters.date) {
    const parsed = new Date(`${filters.date}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return `le ${dayMonthFmt.format(parsed)} ?`;
    }
  }
  switch (filters.when) {
    case "today":
      return "aujourd'hui ?";
    case "tomorrow":
      return "demain ?";
    case "weekend":
      return "ce week-end ?";
    case "week":
      return "cette semaine ?";
    default:
      return "en ce moment ?";
  }
}

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const sort = useMemo(() => parseSort(searchParams), [searchParams]);

  const [val, setVal] = useState(filters.q);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const abortRef = useRef(null);

  // One-time cleanup of legacy localStorage persistence.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Storage disabled — ignore.
    }
  }, []);

  // Keep the input in sync if the URL changes externally (e.g. a tag on Event).
  useEffect(() => {
    setVal(filters.q);
  }, [filters.q]);

  // Without a period filter, hide events that are already over.
  const where = useMemo(() => {
    const base = buildWhereClause(filters);
    if (filters.when || filters.date) return base;
    return base ? `${base} AND ${notFinishedClause()}` : notFinishedClause();
  }, [filters]);

  const runSearch = useCallback(
    (nextOffset, append) => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setIsLoading(true);
      setError(null);

      fetchEvents(
        {
          where: where || undefined,
          limit: PAGE_SIZE,
          offset: nextOffset,
          orderBy: orderByFor(sort),
        },
        ac.signal
      )
        .then((result) => {
          const records = result.records.map((item) => item.record);
          setItems((prev) => (append ? [...prev, ...records] : records));
          setTotalCount(result.total_count ?? records.length);
          setOffset(nextOffset + records.length);
          setIsLoading(false);
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setIsLoading(false);
        });
    },
    [where, sort]
  );

  // The page always shows something: with no filter it lists what is on now.
  useEffect(() => {
    runSearch(0, false);
    return () => abortRef.current?.abort();
  }, [runSearch]);

  const updateFilter = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || v === undefined) next.delete(k);
          else next.set(k, v);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    updateFilter({ q: val.trim() || null });
  };

  const clearQuery = () => {
    setVal("");
    updateFilter({ q: null });
  };

  const resetAll = () => {
    setVal("");
    setSearchParams({});
  };

  const applySuggestion = (params) => {
    setVal("");
    setSearchParams(params);
  };

  const loadMore = () => runSearch(offset, true);

  const todayLabel = useMemo(() => {
    const raw = fullDateFmt.format(new Date());
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, []);

  const activeFilters = useMemo(() => describeActiveFilters(filters), [filters]);

  const filtered = hasAnyFilter(filters);
  const activeCount = countActiveFilters(filters);
  const firstLoad = isLoading && items.length === 0;
  const noResult = !isLoading && !error && items.length === 0;
  const hasMore = items.length < totalCount;

  const sortSelect = (id) => (
    <>
      <label className="pa-small pa-muted" htmlFor={id}>
        Trier par
      </label>
      <select
        id={id}
        className="pa-select"
        value={sort}
        onChange={(e) =>
          updateFilter({
            sort: e.target.value === "date" ? null : e.target.value,
          })
        }
      >
        {SORT_OPTIONS.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <div className="container">
      <header className="pa-search-head">
        <p className="pa-eyebrow">{todayLabel}</p>
        <h1 className="pa-display pa-search-head__title">
          Que faire à Paris{" "}
          <span className="pa-display__accent">{headlineSuffix(filters)}</span>
        </h1>
        <p className="pa-lead prose pa-search-head__lead">
          Concerts, expos, ateliers, balades. Toute la programmation de la Ville
          de Paris, filtrée comme vous voulez.
        </p>

        <form onSubmit={handleSubmit} className="pa-searchbar">
          <label className="visually-hidden" htmlFor="pa-q">
            Rechercher un événement
          </label>
          <div className="pa-field">
            <i className="bi bi-search pa-field__icon" aria-hidden="true"></i>
            <input
              id="pa-q"
              type="search"
              className="pa-field__input"
              placeholder="Jazz, Bourse de Commerce, atelier enfants…"
              value={val}
              onChange={(e) => setVal(e.target.value)}
            />
            {val && (
              <button
                type="button"
                className="pa-field__clear"
                onClick={clearQuery}
                aria-label="Effacer la recherche"
              >
                <i className="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="pa-btn pa-btn--primary pa-searchbar__submit"
            disabled={isLoading}
          >
            {isLoading ? "Recherche…" : "Chercher"}
          </button>
        </form>
      </header>

      {/* Bandeau de filtres — desktop */}
      <div className="d-none d-lg-block">
        <SearchFilters
          filters={filters}
          onChange={updateFilter}
          onReset={resetAll}
          layout="band"
          idPrefix="desktop"
        />
      </div>

      {/* Accès aux filtres — mobile */}
      <div className="d-lg-none pa-mobile-filters">
        <button
          type="button"
          className="pa-btn pa-btn--quiet"
          onClick={() => setDrawerOpen(true)}
        >
          <i className="bi bi-sliders" aria-hidden="true"></i>
          Filtres
          {activeCount > 0 && (
            <span className="pa-mobile-filters__count">{activeCount}</span>
          )}
        </button>
        <div className="pa-mobile-filters__sort">{sortSelect("pa-sort-m")}</div>
      </div>

      {error && (
        <div className="pa-results-error">
          <Error err={error} onRetry={() => runSearch(0, false)} />
        </div>
      )}

      {!error && (
        <div className="pa-results-head">
          <div className="pa-results-head__main">
            <h2 className="pa-results-head__count">
              {firstLoad
                ? "Recherche…"
                : `${totalCount.toLocaleString("fr-FR")} événement${
                    totalCount > 1 ? "s" : ""
                  }${filtered ? "" : " à l'affiche"}`}
            </h2>
            {activeFilters.length > 0 && (
              <div className="pa-chip-row pa-results-head__pills">
                {activeFilters.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="pa-pill"
                    onClick={() => updateFilter(a.patch)}
                    aria-label={`Retirer le filtre ${a.label}`}
                  >
                    {a.label}
                    <i className="bi bi-x-lg pa-pill__x" aria-hidden="true"></i>
                  </button>
                ))}
                <button type="button" className="pa-chip-more" onClick={resetAll}>
                  Tout effacer
                </button>
              </div>
            )}
          </div>

          <div className="pa-results-head__sort d-none d-lg-flex">
            {sortSelect("pa-sort")}
          </div>
        </div>
      )}

      {!filtered && !error && (
        <nav className="pa-suggest" aria-label="Suggestions de recherche">
          <span className="pa-label pa-suggest__label">Une idée de sortie&nbsp;?</span>
          <div className="pa-chip-row">
            {SUGGESTIONS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="pa-chip"
                onClick={() => applySuggestion(s.params)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>
      )}

      {noResult && (
        <EmptyState
          icon="bi-binoculars"
          title="Aucun événement ne correspond"
          description="Essayez d'élargir la période, de retirer un type ou de simplifier votre recherche."
          ctaLabel="Réinitialiser les filtres"
          onCtaClick={resetAll}
        />
      )}

      {firstLoad && !error && <SkeletonGrid count={6} />}

      {!error && items.length > 0 && (
        <div
          className={`row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 pa-grid${
            isLoading ? " is-loading" : ""
          }`}
          aria-busy={isLoading}
        >
          {items.map((item) => (
            <div className="col" key={item.id}>
              <Card content={item} />
            </div>
          ))}
        </div>
      )}

      {!error && !isLoading && hasMore && (
        <div className="pa-loadmore">
          <button
            type="button"
            className="pa-btn pa-btn--ghost pa-btn--pill pa-btn--lg"
            onClick={loadMore}
          >
            Voir {Math.min(PAGE_SIZE, totalCount - items.length)} événements de
            plus
          </button>
        </div>
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeCount={activeCount}
      >
        <SearchFilters
          filters={filters}
          onChange={updateFilter}
          onReset={resetAll}
          layout="stack"
          idPrefix="mobile"
        />
      </FilterDrawer>
    </div>
  );
}

export default Search;
